import { motion } from "framer-motion";
import { Star, ShieldCheck, Clock, BadgeCheck, MapPin } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { PriceEstimator } from "@/components/site/PriceEstimator";
import { Footer } from "@/components/site/Footer";
import { TESTIMONIALS, GALLERY, CONTACT } from "@/data";

const CHIPS = [
  { icon: BadgeCheck, label: "100% Free" },
  { icon: ShieldCheck, label: "No obligation" },
  { icon: Clock, label: "Takes ~1 minute" },
  { icon: MapPin, label: "Locally owned" },
];

const EstimateHero = () => {
  const review = TESTIMONIALS[0];
  const work = GALLERY[0];
  return (
    <section className="relative bg-cream border-b border-ink/10 pt-24 pb-6 lg:pt-32 lg:pb-16" data-testid="estimate-hero">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-7">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-rust">Free Estimate</span>
            <h1 className="mt-3 lg:mt-4 font-display font-black tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[0.98] lg:leading-[0.95] text-ink">
              Get your free garage estimate in about a minute.
            </h1>
            <p className="mt-3 lg:mt-5 text-base lg:text-lg text-ink/70 leading-relaxed max-w-xl">
              Answer a few quick questions and we'll build your custom, no-obligation price on the spot. No spam.
            </p>

            <div className="mt-4 lg:mt-7 flex flex-wrap gap-2" data-testid="estimate-hero-chips">
              {CHIPS.map((c) => (
                <span key={c.label} className="inline-flex items-center gap-1.5 lg:gap-2 bg-surface border border-ink/15 px-2.5 lg:px-3.5 py-1.5 lg:py-2 text-xs lg:text-sm font-medium text-ink">
                  <c.icon size={14} className="text-rust" /> {c.label}
                </span>
              ))}
            </div>

            <div className="mt-4 lg:mt-7 flex items-center gap-3">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-rust text-rust" />)}</div>
              <a href={CONTACT.googleReviewUrl} target="_blank" rel="noreferrer"
                className="font-mono text-xs text-ink/70 hover:text-rust transition-colors" data-testid="estimate-hero-rating">
                5.0 on Google · Loved by local homeowners
              </a>
            </div>

            {/* compact before/after — mobile only */}
            <div className="mt-5 lg:hidden border border-ink/15 bg-surface overflow-hidden" data-testid="estimate-hero-beforeafter-mobile">
              <div className="grid grid-cols-2">
                <div className="relative">
                  <img src={work.before} alt="Garage before" loading="lazy" decoding="async" className="w-full h-24 object-cover" />
                  <span className="absolute top-1.5 left-1.5 bg-ink/80 text-paper text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5">Before</span>
                </div>
                <div className="relative">
                  <img src={work.after} alt="Garage after" loading="lazy" decoding="async" className="w-full h-24 object-cover" />
                  <span className="absolute top-1.5 left-1.5 bg-rust text-white text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5">After</span>
                </div>
              </div>
              <p className="px-3 py-2 text-[11px] text-ink/70">"{review.body}" <span className="font-semibold text-ink">— {review.name}</span></p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="hidden lg:block lg:col-span-5">
            <div className="border border-ink/15 bg-surface overflow-hidden">
              <div className="grid grid-cols-2">
                <div className="relative">
                  <img src={work.before} alt="Garage before" loading="lazy" decoding="async" className="w-full h-40 object-cover" />
                  <span className="absolute top-2 left-2 bg-ink/80 text-paper text-[10px] font-mono uppercase tracking-widest px-2 py-1">Before</span>
                </div>
                <div className="relative">
                  <img src={work.after} alt="Garage after" loading="lazy" decoding="async" className="w-full h-40 object-cover" />
                  <span className="absolute top-2 left-2 bg-rust text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">After</span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-rust text-rust" />)}</div>
                <p className="mt-2 text-sm text-ink/80 leading-relaxed">"{review.body}"</p>
                <p className="mt-2 text-xs font-semibold text-ink">— {review.name} <span className="font-normal text-ink/40">· {work.name}</span></p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function Estimate() {
  return (
    <div className="bg-paper">
      <Navbar />
      <EstimateHero />
      <PriceEstimator />
      <Footer />
    </div>
  );
}
